export const AUTH0_CONNECTIONS = {
    DATABASE: {
        name: 'Username-Password-Authentication',
        label: 'Email/Password',
        buttonLabel: 'Email/Password',
    },
    AZURE_AD: {
        name: 'curium-ad',
        label: 'Azure AD SSO',
        buttonLabel: 'Azure AD',
    },
} as const;

export type Auth0Connection = (typeof AUTH0_CONNECTIONS)[keyof typeof AUTH0_CONNECTIONS]['name'];

/**
 * Search param naming the Auth0 connection `/login` should go straight to, skipping Universal Login's
 * password form. Emailed deep links carry it too: `RequireAuth` keeps it inside `redirectTo` (CUR-1790).
 */
export const LOGIN_CONNECTION_SEARCH_PARAM = 'connection';

/**
 * What someone is told when their Microsoft sign-in is refused because it is still linked to an old
 * profile while their new profile waits (CUR-2873). The login bootstrap sends it with the
 * `sso_linked_to_old_profile` refusal; the client's auth error map shows the same words.
 */
export const SSO_LINKED_TO_OLD_PROFILE_MESSAGE =
    'Your Microsoft account is still linked to an older Curium profile that no longer has access. Please ask your administrator to contact Curium support to move your sign-in to your new profile.';

export type Auth0LogEventCategory =
    | 'action'
    | 'login'
    | 'logout'
    | 'mfa'
    | 'signup'
    | 'silent_authentication'
    | 'system'
    | 'token_exchange'
    | 'user';

export type Auth0LogEventOutcome = 'failure' | 'notification' | 'success';

export interface Auth0LogEventDefinition {
    category: Auth0LogEventCategory;
    message: string;
    outcome: Auth0LogEventOutcome;
    warning?: boolean;
}

/**
 * Shared presentation and severity contract for Auth0 event codes emitted by the recommended
 * Log Stream categories. Unknown codes remain valid and use the sink/UI fallback presentation.
 */
export const AUTH0_LOG_EVENT_DEFINITIONS: Readonly<Record<string, Auth0LogEventDefinition>> = {
    // Action Execution - Failure
    actions_execution_failed: {
        category: 'action',
        message: 'Auth0 Action execution failed',
        outcome: 'failure',
    },
    // Login - Success / Failure / Notification
    s: { category: 'login', message: 'Auth0 login succeeded', outcome: 'success' },
    scoa: { category: 'login', message: 'Auth0 cross-origin login succeeded', outcome: 'success' },
    sens: { category: 'login', message: 'Auth0 native social login succeeded', outcome: 'success' },
    f: { category: 'login', message: 'Auth0 login failed', outcome: 'failure' },
    fc: { category: 'login', message: 'Auth0 login failed: connector', outcome: 'failure' },
    fco: { category: 'login', message: 'Auth0 login failed: origin not allowed', outcome: 'failure' },
    fcoa: { category: 'login', message: 'Auth0 cross-origin login failed', outcome: 'failure' },
    fens: { category: 'login', message: 'Auth0 native social login failed', outcome: 'failure' },
    fp: { category: 'login', message: 'Auth0 login failed: incorrect password', outcome: 'failure' },
    fu: { category: 'login', message: 'Auth0 login failed: unknown user', outcome: 'failure' },
    w: { category: 'login', message: 'Auth0 login warning', outcome: 'notification', warning: true },
    pwd_leak: { category: 'login', message: 'Auth0 blocked login: breached password', outcome: 'failure' },
    // Logout - Success / Failure
    slo: { category: 'logout', message: 'Auth0 logout succeeded', outcome: 'success' },
    oidc_backchannel_logout_succeeded: {
        category: 'logout',
        message: 'Auth0 back-channel logout succeeded',
        outcome: 'success',
    },
    flo: { category: 'logout', message: 'Auth0 logout failed', outcome: 'failure' },
    oidc_backchannel_logout_failed: {
        category: 'logout',
        message: 'Auth0 back-channel logout failed',
        outcome: 'failure',
    },
    // Signup - Success / Failure
    ss: { category: 'signup', message: 'Auth0 signup succeeded', outcome: 'success' },
    fs: { category: 'signup', message: 'Auth0 signup failed', outcome: 'failure' },
    // Silent Authentication
    ssa: { category: 'silent_authentication', message: 'Auth0 silent authentication succeeded', outcome: 'success' },
    fsa: { category: 'silent_authentication', message: 'Auth0 silent authentication failed', outcome: 'failure' },
    // Token Exchange - Failure
    feacft: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: authorization code',
        outcome: 'failure',
    },
    feccft: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: client credentials',
        outcome: 'failure',
    },
    fede: { category: 'token_exchange', message: 'Auth0 token exchange failed: device code', outcome: 'failure' },
    feoobft: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: password + OOB challenge',
        outcome: 'failure',
    },
    feotpft: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: password + OTP challenge',
        outcome: 'failure',
    },
    fepft: { category: 'token_exchange', message: 'Auth0 token exchange failed: password', outcome: 'failure' },
    fepotpft: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: passwordless OTP',
        outcome: 'failure',
    },
    fercft: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: password + MFA recovery code',
        outcome: 'failure',
    },
    ferrt: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: rotating refresh token',
        outcome: 'failure',
    },
    fertft: {
        category: 'token_exchange',
        message: 'Auth0 token exchange failed: refresh token',
        outcome: 'failure',
    },
    // System - Notification
    limit_wc: {
        category: 'system',
        message: 'Auth0 blocked IP: too many failed logins into one account',
        outcome: 'failure',
    },
    limit_mu: {
        category: 'system',
        message: 'Auth0 blocked IP: too many failed logins or signups',
        outcome: 'failure',
    },
    limit_delegation: { category: 'system', message: 'Auth0 rate limit: /delegation', outcome: 'failure' },
    api_limit: { category: 'system', message: 'Auth0 API rate limit reached', outcome: 'failure' },
    admin_update_launch: { category: 'system', message: 'Auth0 update launched', outcome: 'notification' },
    depnote: { category: 'system', message: 'Auth0 deprecation notice', outcome: 'notification' },
    coff: { category: 'system', message: 'Auth0 AD/LDAP connector offline', outcome: 'failure' },
    con: { category: 'system', message: 'Auth0 AD/LDAP connector online', outcome: 'notification' },
    fcpro: { category: 'system', message: 'Auth0 AD/LDAP connector provisioning failed', outcome: 'failure' },
    fui: { category: 'system', message: 'Auth0 user import failed', outcome: 'failure' },
    sys_os_update_start: { category: 'system', message: 'Auth0 OS update started', outcome: 'notification' },
    sys_os_update_end: { category: 'system', message: 'Auth0 OS update ended', outcome: 'notification' },
    sys_update_start: { category: 'system', message: 'Auth0 update started', outcome: 'notification' },
    sys_update_end: { category: 'system', message: 'Auth0 update ended', outcome: 'notification' },
    // User/Behavioral - Success / Failure / Notification
    sce: { category: 'user', message: 'Auth0 email change succeeded', outcome: 'success' },
    fce: { category: 'user', message: 'Auth0 email change failed', outcome: 'failure' },
    scp: { category: 'user', message: 'Auth0 password change succeeded', outcome: 'success' },
    fcp: { category: 'user', message: 'Auth0 password change failed', outcome: 'failure' },
    scpr: { category: 'user', message: 'Auth0 password change request succeeded', outcome: 'success' },
    fcpr: { category: 'user', message: 'Auth0 password change request failed', outcome: 'failure' },
    scpn: { category: 'user', message: 'Auth0 phone number change succeeded', outcome: 'success' },
    fcpn: { category: 'user', message: 'Auth0 phone number change failed', outcome: 'failure' },
    scu: { category: 'user', message: 'Auth0 username change succeeded', outcome: 'success' },
    fcu: { category: 'user', message: 'Auth0 username change failed', outcome: 'failure' },
    sdu: { category: 'user', message: 'Auth0 user deleted', outcome: 'success' },
    fdu: { category: 'user', message: 'Auth0 user deletion failed', outcome: 'failure' },
    sv: { category: 'user', message: 'Auth0 email verification link consumed', outcome: 'success' },
    fv: { category: 'user', message: 'Auth0 verification email failed to send', outcome: 'failure' },
    svr: { category: 'user', message: 'Auth0 verification email queued', outcome: 'success' },
    fvr: { category: 'user', message: 'Auth0 verification email request failed', outcome: 'failure' },
    fn: { category: 'user', message: 'Auth0 email notification failed to send', outcome: 'failure' },
    srrt: { category: 'user', message: 'Auth0 refresh token revoked', outcome: 'success' },
    sui: { category: 'user', message: 'Auth0 users imported', outcome: 'success' },
    fd: { category: 'user', message: 'Auth0 delegation token failed', outcome: 'failure' },
    fdeaz: { category: 'user', message: 'Auth0 device authorization request failed', outcome: 'failure' },
    fdecc: {
        category: 'user',
        message: 'Auth0 device authorization not confirmed by user',
        outcome: 'failure',
    },
    ss_sso_success: { category: 'user', message: 'Auth0 self-service SSO operation succeeded', outcome: 'success' },
    ss_sso_failure: { category: 'user', message: 'Auth0 self-service SSO operation failed', outcome: 'failure' },
    ss_sso_info: { category: 'user', message: 'Auth0 self-service SSO information', outcome: 'notification' },
    cs: { category: 'user', message: 'Auth0 passwordless code sent', outcome: 'notification' },
    ublkdu: { category: 'user', message: 'Auth0 anomaly-detection block released', outcome: 'notification' },
    // Guardian verification codes are unclassified ("Other") in Auth0's filter UI.
    gd_start_enroll: { category: 'mfa', message: 'Auth0 MFA enrolment started', outcome: 'notification' },
    gd_enrollment_complete: { category: 'mfa', message: 'Auth0 MFA enrolment completed', outcome: 'notification' },
    gd_unenroll: { category: 'mfa', message: 'Auth0 MFA device unenrolled', outcome: 'notification' },
    gd_update_device_account: { category: 'mfa', message: 'Auth0 MFA device updated', outcome: 'notification' },
    gd_auth_succeed: { category: 'mfa', message: 'Auth0 MFA succeeded', outcome: 'success' },
    gd_auth_failed: { category: 'mfa', message: 'Auth0 MFA failed', outcome: 'failure' },
    gd_auth_rejected: { category: 'mfa', message: 'Auth0 MFA rejected', outcome: 'failure' },
    gd_recovery_succeed: { category: 'mfa', message: 'Auth0 MFA recovery succeeded', outcome: 'success' },
    gd_recovery_failed: { category: 'mfa', message: 'Auth0 MFA recovery failed', outcome: 'failure' },
    gd_otp_rate_limit_exceed: { category: 'mfa', message: 'Auth0 MFA rate limit exceeded', outcome: 'failure' },
    gd_send_sms_failure: { category: 'mfa', message: 'Auth0 MFA SMS delivery failed', outcome: 'failure' },
    gd_send_voice_failure: { category: 'mfa', message: 'Auth0 MFA voice delivery failed', outcome: 'failure' },
    gd_send_email_failure: { category: 'mfa', message: 'Auth0 MFA email delivery failed', outcome: 'failure' },
    gd_send_pn_failure: { category: 'mfa', message: 'Auth0 MFA push delivery failed', outcome: 'failure' },
} satisfies Record<string, Auth0LogEventDefinition>;
